import { groupByRegion, countryInfo, regionKeyOf, REGIONS } from '../countries.js'

/**
 * Turning what a product sends into flows — {nodes, links} for the Flow (Sankey) chart. Each one
 * conserves the whole: what enters a node leaves it, so the widths can be read as amounts.
 *
 *   regionFlow   all  →  region  →  country               one figure per country
 *   sourceFlow   source columns → region → country       e.g. Domestic and International per country
 *   seriesFlow   series → label                          a stacked bar (each bar made of the series)
 *   pairFlow     column A → column B                     a table of from / to / amount rows
 *   linkFlow     the product's own links                 a chart of kind "flow"
 *
 * A long tail of countries is shown as "Other <region> (n)" per region, so the chart stays
 * readable; the card's Table view always has every country.
 */
const NEUTRAL = '#52525B'
const MAX_COUNTRIES = 18

function countryNodes(groups, prefix = 'c') {
  const all = groups.flatMap(g => g.items.map(it => ({ ...it, g })))
  const keep = new Set(all.sort((a, b) => b.value - a.value).slice(0, MAX_COUNTRIES).map(it => it.name))
  const nodes = [], target = {}
  groups.forEach(g => {
    // A lone leftover is shown as itself: "Other Caribbean (1)" would hide a name for nothing.
    const extra = g.items.filter(it => !keep.has(it.name))
    const rest = extra.length > 1 ? extra : []
    g.items.filter(it => keep.has(it.name) || extra.length === 1).forEach(it => {
      const id = `${prefix}:${it.name}`
      nodes.push({ id, name: it.name, color: g.color, iso2: it.iso2 })
      target[it.name] = id
    })
    if (rest.length) {
      const id = `${prefix}:other:${g.key}`
      nodes.push({ id, name: `Other ${g.short} (${rest.length})`, color: g.color, other: true })
      rest.forEach(it => { target[it.name] = id })
    }
  })
  return { nodes, target }
}

export function regionFlow(items, rootName) {
  const groups = groupByRegion(items)
  if (!groups.length) return null
  const { nodes: cs, target } = countryNodes(groups)
  const nodes = [{ id: 'root', name: rootName, color: NEUTRAL }, ...groups.map(g => ({ id: `r:${g.key}`, name: g.name, short: g.short, color: g.color })), ...cs]
  const links = []
  groups.forEach(g => {
    links.push({ source: 'root', target: `r:${g.key}`, value: g.total })
    const acc = {}
    g.items.forEach(it => { acc[target[it.name]] = (acc[target[it.name]] || 0) + it.value })
    Object.entries(acc).forEach(([t, v]) => links.push({ source: `r:${g.key}`, target: t, value: v }))
  })
  return { nodes, links }
}

/** `parts` = [{name, values: [per row]}], `names` = the row's country names. */
export function sourceFlow(parts, names, colors) {
  const totals = names.map((n, i) => ({ name: n, value: parts.reduce((a, p) => a + (Number(p.values[i]) > 0 ? Number(p.values[i]) : 0), 0) }))
  const groups = groupByRegion(totals)
  if (!groups.length) return null
  const regionOf = {}
  groups.forEach(g => g.items.forEach(it => { regionOf[it.name] = g.key }))
  const { nodes: cs, target } = countryNodes(groups)
  const nodes = [
    ...parts.map((p, k) => ({ id: `s:${p.name}`, name: p.name, color: colors[Math.min(k, colors.length - 1)] })),
    ...groups.map(g => ({ id: `r:${g.key}`, name: g.name, short: g.short, color: g.color })),
    ...cs,
  ]
  const acc = {}
  const add = (s, t, v) => { if (v > 0) { const k = `${s}>${t}`; acc[k] = (acc[k] || 0) + v } }
  names.forEach((n, i) => {
    const rk = regionOf[n]
    if (!rk) return
    parts.forEach(p => {
      const v = Number(p.values[i])
      add(`s:${p.name}`, `r:${rk}`, v)
      add(`r:${rk}`, target[n], v)
    })
  })
  const links = Object.entries(acc).map(([k, value]) => { const [source, t] = k.split('>'); return { source, target: t, value } })
  return { nodes, links }
}

export function seriesFlow(labels, series, colors) {
  const nodes = series.map((s, k) => ({ id: `s:${s.name}`, name: s.name, color: colors[Math.min(k, colors.length - 1)] }))
    .concat(labels.map(l => {
      const info = countryInfo(l)
      return { id: `t:${l}`, name: String(l), color: NEUTRAL, iso2: info?.iso2 }
    }))
  const links = []
  series.forEach(s => labels.forEach((l, i) => {
    const v = Number(s.values[i])
    if (v > 0) links.push({ source: `s:${s.name}`, target: `t:${l}`, value: v })
  }))
  return links.length ? { nodes, links } : null
}

export function pairFlow(rows, a, b, valueAt, colors) {
  const acc = {}, totals = {}
  rows.forEach(r => {
    const s = String(r[a] ?? '').trim(), t = String(r[b] ?? '').trim(), v = Number(valueAt(r))
    if (!s || !t || !(v > 0)) return
    acc[`${s}\u0000${t}`] = (acc[`${s}\u0000${t}`] || 0) + v
    totals[s] = (totals[s] || 0) + v
  })
  const sources = Object.keys(totals).sort((x, y) => totals[y] - totals[x])
  if (!sources.length) return null
  const colorOf = {}
  sources.forEach((s, k) => { colorOf[s] = colors[Math.min(k, colors.length - 1)] })
  const targets = [...new Set(Object.keys(acc).map(k => k.split('\u0000')[1]))]
  return {
    nodes: [
      ...sources.map(s => ({ id: `a:${s}`, name: s, color: colorOf[s], iso2: countryInfo(s)?.iso2 })),
      ...targets.map(t => ({ id: `b:${t}`, name: t, color: NEUTRAL, iso2: countryInfo(t)?.iso2 })),
    ],
    links: Object.entries(acc).map(([k, value]) => { const [s, t] = k.split('\u0000'); return { source: `a:${s}`, target: `b:${t}`, value } }),
  }
}

/** A chart of kind "flow": links [{source, target, value}] by name. */
export function linkFlow(links, colors) {
  const clean = (Array.isArray(links) ? links : [])
    .map(l => ({ s: String(l?.source ?? '').trim(), t: String(l?.target ?? '').trim(), v: Number(l?.value) }))
    .filter(l => l.s && l.t && l.s !== l.t && l.v > 0)
  if (!clean.length) return null
  const names = [...new Set(clean.flatMap(l => [l.s, l.t]))]
  const out = {}
  clean.forEach(l => { out[l.s] = (out[l.s] || 0) + l.v })
  const order = Object.keys(out).sort((a, b) => out[b] - out[a])
  const colorOf = {}
  order.forEach((n, k) => { colorOf[n] = colors[Math.min(k, colors.length - 1)] })
  // A node that names a UNAIDS region, or a country, takes that region's colour, as everywhere else.
  const regionColour = (n) => REGIONS[regionKeyOf(n) || countryInfo(n)?.region]?.color
  return {
    nodes: names.map(n => ({ id: n, name: n, color: colorOf[n] || regionColour(n) || NEUTRAL, iso2: countryInfo(n)?.iso2 })),
    links: clean.map(l => ({ source: l.s, target: l.t, value: l.v })),
  }
}
